import { parse } from 'papaparse'
import { defineStore } from 'pinia'

import { commercialApi } from '@/interfaces/spark-api'
import { ref } from 'vue'
import { components } from '#/types/capi-generated'

export const useAccessStore = defineStore('access', () => {
  const hasAccessPermission = ref(false)

  function parseCsv(csv: string): { columns: string[]; rows: string[][] } {
    const { data, meta, errors } = parse<string>(csv, {
      header: true,
      delimiter: ',',
      skipEmptyLines: true,
    })

    if (errors.length > 0) {
      console.error('CSV parse errors:', errors)
    }

    const columns = meta.fields ?? []
    const rows = data
      .map((item) => Object.values(item))
      .filter((item) => item.length === columns.length)

    return { columns, rows }
  }

  async function fetchLatestSparkRCsv(
    perVesselSize: components['schemas']['RegasCostsVesselSizeEnum'],
    unit: components['schemas']['RegasCostsUnitEnum']
  ): Promise<{
    columns: string[]
    rows: string[][]
  }> {
    try {
      const terminalsCsv = await commercialApi.fetchLatestSparkRCsv(
        perVesselSize,
        unit
      )

      hasAccessPermission.value = true
      return parseCsv(terminalsCsv)
    } catch (err) {
      console.error(err)
      return { columns: [], rows: [] }
    }
  }

  async function fetchLastNSparkRCsv(
    perVesselSize: components['schemas']['RegasCostsVesselSizeEnum'],
    unit: components['schemas']['RegasCostsUnitEnum'],
    start: string,
    end: string,
    n: number
  ): Promise<{
    columns: string[]
    rows: string[][]
  }> {
    try {
      const gasHubsCsv = await commercialApi.fetchSparkRListCsv(
        perVesselSize,
        unit,
        start,
        end,
        n,
        0
      )
      return parseCsv(gasHubsCsv)
    } catch (err) {
      console.error(err)
      return { columns: [], rows: [] }
    }
  }

  async function fetchAllSparkRCsv(
    perVesselSize: components['schemas']['RegasCostsVesselSizeEnum'],
    unit: components['schemas']['RegasCostsUnitEnum'],
    start: string,
    end: string
  ): Promise<{
    columns: string[]
    rows: string[][]
  }> {
    try {
      const gasHubsCsv = await commercialApi.fetchSparkRListCsv(
        perVesselSize,
        unit,
        start,
        end,
        999999,
        0
      )
      return parseCsv(gasHubsCsv)
    } catch (err) {
      console.error(err)
      return { columns: [], rows: [] }
    }
  }

  async function fetchDesHubNetbacksCsv(
    unit: components['schemas']['DesHubNetbacksUnit'],
    start: string,
    end: string
  ): Promise<{
    columns: string[]
    rows: string[][]
  }> {
    try {
      const csv = await commercialApi.fetchDesHubNetbacksListCsv(
        unit,
        start,
        end
      )
      return parseCsv(csv)
    } catch (err) {
      console.error(err)
      return { columns: [], rows: [] }
    }
  }

  async function fetchLatestDesHubNetbacksCsv(
    unit: components['schemas']['DesHubNetbacksUnit']
  ): Promise<{
    columns: string[]
    rows: string[][]
  }> {
    try {
      const csv = await commercialApi.fetchLatestDesHubNetbacksCsv(unit)
      return parseCsv(csv)
    } catch (err) {
      console.error(err)
      return { columns: [], rows: [] }
    }
  }

  return {
    hasAccessPermission,
    fetchLatestSparkRCsv,
    fetchLastNSparkRCsv,
    fetchAllSparkRCsv,
    fetchDesHubNetbacksCsv,
    fetchLatestDesHubNetbacksCsv,
  }
})
